import { z } from 'zod';

import { CustomerListMatchingAttribute, CustomFieldType } from '~/types/api.graphql';

import {
  CUSTOMER_LIST_KEPT_COLUMNS_KIND,
  CUSTOMER_LIST_MAPPING_KIND,
  CustomerListColumn,
  CustomerListKeptColumnsValue,
  CustomerListKeptField,
  CustomerListMappingValue,
  CustomerListMatchingField,
  CustomerListMatchingLimits,
} from './types';

// Cast to the module's types: without `strictNullChecks`, zod infers every object key as optional and tuples as
// open-ended
const attributeSchema = z.nativeEnum(CustomerListMatchingAttribute);
const columnIndexSchema = z.number().int().nonnegative();

export const customerListColumnsSchema = z.array(
  z.object({
    index: columnIndexSchema,
    columnName: z.string(),
    suggestedAttribute: attributeSchema.optional(),
    suggestedFieldType: z.nativeEnum(CustomFieldType).optional(),
  }),
) as unknown as z.ZodType<CustomerListColumn[]>;

export const customerListMatchingLimitsSchema = z.object({
  maxRules: z.number().int().positive(),
  maxFieldsPerRule: z.number().int().positive(),
  identifierAttributes: z.array(attributeSchema),
}) as unknown as z.ZodType<CustomerListMatchingLimits>;

const matchingFieldSchema = z.tuple([columnIndexSchema, z.string(), attributeSchema]);

export const customerListMappingValueSchema = z.object({
  kind: z.literal(CUSTOMER_LIST_MAPPING_KIND),
  attachmentId: z.string(),
  rules: z.array(z.array(matchingFieldSchema)),
  unused: z.array(matchingFieldSchema),
  identifierAttributes: z.array(attributeSchema),
}) as unknown as z.ZodType<CustomerListMappingValue>;

/** A starting value the agent builds or copies back: only its attachment id is checked here, and each field on its
 * own, so one malformed field drops only its rule. `unused` comes only from a submitted value being reopened. */
export const customerListMappingStartSchema = z.object({
  attachmentId: z.string(),
  rules: z.array(z.unknown()),
  unused: z.array(z.unknown()).optional(),
}) as unknown as z.ZodType<{ attachmentId: string; rules: unknown[]; unused?: unknown[] }>;

/** One matching field as `[index, columnName, attribute]`; `null` when it has another shape or attribute. */
export const parseMatchingField = (field: unknown) => {
  const parsed = matchingFieldSchema.safeParse(field);
  return parsed.success ? (parsed.data as CustomerListMatchingField) : null;
};

const keptFieldSchema = z.tuple([columnIndexSchema, z.string(), z.string(), z.nativeEnum(CustomFieldType)]);

export const customerListKeptColumnsValueSchema = z.object({
  kind: z.literal(CUSTOMER_LIST_KEPT_COLUMNS_KIND),
  attachmentId: z.string(),
  fields: z.array(keptFieldSchema),
}) as unknown as z.ZodType<CustomerListKeptColumnsValue>;

/** A starting value the agent builds: only its attachment id is checked here, and each field on its own, so one
 * malformed field doesn't drop the others. */
export const customerListKeptColumnsStartSchema = z.object({
  attachmentId: z.string(),
  fields: z.array(z.unknown()),
}) as unknown as z.ZodType<{ attachmentId: string; fields: unknown[] }>;

/** One kept field as `[index, columnName, savedName, type]`; `null` when it has another shape or type. */
export const parseKeptField = (field: unknown) => {
  const parsed = keptFieldSchema.safeParse(field);
  return parsed.success ? (parsed.data as CustomerListKeptField) : null;
};

/**
 * Parses one of the components' JSON-string props or values; `null` when it is empty or doesn't fit the schema. Also
 * takes the value as an object, as the agent may initialize a form's data model with one.
 */
export const parseJsonProp = <T>(schema: z.ZodType<T>, json: unknown): T | null => {
  if (!json) return null;
  try {
    const parsed = schema.safeParse(typeof json === 'string' ? JSON.parse(json) : json);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};
